import { LngLat, MercatorCoordinate, type Map as MapLibreMap } from 'maplibre-gl';
import type { CameraPadding } from '../domain/camera-plan';

export interface GimbalCameraSample {
  ground: [number, number]; center: [number, number]; altitude: number; aim: number;
}
/** Interpolates complete camera and aim positions in the map's three-dimensional space. */
export function blendGimbalCameras(from: GimbalCameraSample, to: GimbalCameraSample, amount: number): GimbalCameraSample {
  if (amount <= 0) return from;
  if (amount >= 1) return to;
  const blend = (a: [number, number], aHeight: number, b: [number, number], bHeight: number) => {
    const start = MercatorCoordinate.fromLngLat(a, aHeight), finish = MercatorCoordinate.fromLngLat(b, bHeight);
    const dx = ((finish.x - start.x + 1.5) % 1) - .5;
    const point = new MercatorCoordinate(start.x + dx * amount, start.y + (finish.y - start.y) * amount, start.z + (finish.z - start.z) * amount);
    const coordinate = point.toLngLat();
    return { coordinate: [((coordinate.lng + 540) % 360) - 180, coordinate.lat] as [number, number], height: point.toAltitude() };
  };
  const camera = blend(from.ground, from.altitude, to.ground, to.altitude), aim = blend(from.center, from.aim, to.center, to.aim);
  return { ground: camera.coordinate, altitude: camera.height, center: aim.coordinate, aim: aim.height };
}
/** Keeps the installed engine's height access behind one checked boundary. */
export function readGimbalCamera(map: MapLibreMap): GimbalCameraSample {
  const transform = (map as unknown as { _camera?: { transform?: {
    getCameraLngLat?: () => { lng: number; lat: number }; getCameraAltitude?: () => number;
  } } })._camera?.transform;
  if (!transform?.getCameraLngLat || !transform.getCameraAltitude) throw new Error('Camera Gimbal is incompatible with this map engine.');
  const ground = transform.getCameraLngLat(), center = map.getCenter();
  const sample: GimbalCameraSample = { ground: [ground.lng, ground.lat], center: [center.lng, center.lat],
    altitude: transform.getCameraAltitude(), aim: map.getCenterElevation() };
  if (![...sample.ground, ...sample.center, sample.altitude, sample.aim].every(Number.isFinite)) throw new Error('Camera Gimbal received invalid terrain heights.');
  return sample;
}
export function applyGimbalCamera(map: MapLibreMap, sample: GimbalCameraSample, padding: CameraPadding): void {
  map.setCenterClampedToGround(false);
  const options = map.calculateCameraOptionsFromTo(new LngLat(...sample.ground), sample.altitude, new LngLat(...sample.center), sample.aim);
  map.jumpTo({ ...options, elevation: sample.aim, padding });
}
